import type { TFunction } from "i18next";

/**
 * Converts a timestamp to a relative time string (e.g., "5m", "2h", "3d")
 * Uses i18n for localized suffixes
 */
export const formatTimeAgo = (timestamp: string, t: TFunction): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));

  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${t("beneficiary_detail.activity.minutes")} ${t("beneficiary_detail.activity.ago")}`;
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours} ${t("beneficiary_detail.activity.hours")} ${t("beneficiary_detail.activity.ago")}`;
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    return `${days} ${t("beneficiary_detail.activity.days")} ${t("beneficiary_detail.activity.ago")}`;
  }
};

export const getRelativeTime = (timestamp: string, t: TFunction): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return t("notifications.just_now");
  if (diffInMinutes < 60) return `${diffInMinutes}${t("notifications.minutes_ago")}`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}${t("notifications.hours_ago")}`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}${t("notifications.days_ago")}`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) return `${diffInWeeks}${t("notifications.weeks_ago", "sem")}`;

  return date.toLocaleDateString();
};

/**
 * "today / yesterday / N days ago / N weeks ago / DD MMM" — used by the
 * templates list and detail panel. Locale falls back to the active i18n
 * language so the absolute fallback respects the user's preference.
 */
export const formatTemplateRelativeDate = (
  dateStr: string | undefined,
  t: TFunction,
  locale?: string,
): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t("templates.today");
  if (diffDays === 1) return t("templates.yesterday");
  if (diffDays < 7) return t("templates.days_ago", { count: diffDays });
  if (diffDays < 30) return t("templates.weeks_ago", { count: Math.floor(diffDays / 7) });
  return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
};
